import React, { useMemo } from "react";
import { Card, Button, Badge, ButtonGroup } from "react-bootstrap";
import { usePendingPriceChanges } from "../../hooks/usePendingPriceChanges";
import { useApplyPriceChange } from "../../hooks/useApplyPriceChange";
import { useCancelPriceChange } from "../../hooks/useCancelPriceChange";
import DataTable from "components/common/DataTable";
import { createColumn } from "components/common/DataTable/utils";
import InitialLoader from "components/InitilalLoader";
import moment from "moment";

const PendingPriceChangesList = () => {
  const { data, isLoading, error, refetch } = usePendingPriceChanges({ status: "pending" });
  const { mutate: applyChange, isPending: isApplying } = useApplyPriceChange();
  const { mutate: cancelChange, isPending: isCancelling } = useCancelPriceChange();

  const handleApply = (changeId, companyId) => {
    if (window.confirm("Are you sure you want to apply this price change?")) {
      applyChange({ changeId, companyId }, {
        onSuccess: () => {
          refetch();
        },
      });
    }
  };

  const handleCancel = (changeId) => {
    const reason = window.prompt("Enter reason for cancellation (optional):");
    cancelChange(
      {
        changeId,
        body: reason ? { reason } : {},
      },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  const columns = useMemo(
    () => [
      createColumn({
        id: "companyName",
        header: "Company Name",
        accessorFn: (row) => row.companyId?.companyName || row.companyId?._id || "N/A",
        cell: ({ row }) => row.original.companyId?.companyName || row.original.companyId?._id || "N/A",
        enableSorting: true,
      }),
      createColumn({
        id: "companyEmail",
        header: "Company Email",
        accessorFn: (row) => row.companyId?.companyEmail || "N/A",
        cell: ({ row }) => row.original.companyId?.companyEmail || "N/A",
        enableSorting: true,
      }),
      createColumn({
        id: "oldPrice",
        header: "Old Price",
        accessorKey: "oldPrice",
        cell: ({ row }) => `$${row.original.oldPrice?.toFixed(2) || "0.00"}`,
        enableSorting: true,
      }),
      createColumn({
        id: "newPrice",
        header: "New Price",
        accessorKey: "newPrice",
        cell: ({ row }) => `$${row.original.newPrice?.toFixed(2) || "0.00"}`,
        enableSorting: true,
      }),
      createColumn({
        id: "noticeDate",
        header: "Notice Date",
        accessorKey: "noticeDate",
        cell: ({ row }) =>
          row.original.noticeDate
            ? moment(row.original.noticeDate).format("MMM DD, YYYY")
            : "N/A",
        enableSorting: true,
      }),
      createColumn({
        id: "effectiveDate",
        header: "Effective Date",
        accessorKey: "effectiveDate",
        cell: ({ row }) =>
          row.original.effectiveDate
            ? moment(row.original.effectiveDate).format("MMM DD, YYYY")
            : "N/A",
        enableSorting: true,
      }),
      createColumn({
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <Badge bg={row.original.status === "pending" ? "warning" : "success"}>
            {row.original.status}
          </Badge>
        ),
        enableSorting: true,
      }),
      createColumn({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <ButtonGroup size="sm">
            <Button
              variant="success"
              onClick={() => handleApply(row.original._id, row.original.companyId?._id)}
              disabled={isApplying || isCancelling}
            >
              Apply
            </Button>
            <Button
              variant="danger"
              onClick={() => handleCancel(row.original._id)}
              disabled={isApplying || isCancelling}
            >
              Cancel
            </Button>
          </ButtonGroup>
        ),
        enableSorting: false,
      }),
    ],
    [isApplying, isCancelling]
  );

  if (isLoading) {
    return <InitialLoader />;
  }

  if (error) {
    return <div className="text-danger">Error loading pending price changes: {error.message}</div>;
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <Card.Body>
          <p className="text-muted">No pending price changes.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <h5>Pending Price Changes</h5>
      </Card.Header>
      <Card.Body>
        <DataTable
          data={data}
          columns={columns}
          loading={isLoading}
          pagination={{ enabled: true, pageSize: 10 }}
          sorting={{ enabled: true }}
          keyField="_id"
          striped
          bordered
          hover
          responsive
        />
      </Card.Body>
    </Card>
  );
};

export default PendingPriceChangesList;
